import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [submitUrl, setSubmitUrl] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
  e.preventDefault();

  try {
    const response = await fetch('/api/send-email.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
      }).toString(),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to send message');
    }

    setSent(true);
  } catch (error) {
    console.error('Contact form error:', error);
    alert('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.');
  }
}, [form]);
 
  

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-28 md:pt-32">
        {/* Hero */}
        <section className="bg-primary-900 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 lg:px-12 xl:px-20 text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">{t('contact_title')}</h1>
            <p className="text-background-300 text-sm md:text-base max-w-2xl mx-auto">{t('contact_subtitle')}</p>
          </div>
        </section>

        <div className="w-full px-4 md:px-6 lg:px-12 xl:px-20 py-10">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Contact Form */}
            <div className="flex-1">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">{t('contact_form_title')}</h2>
              {sent ? (
                <div className="bg-accent-50 border border-accent-200 rounded-sm p-8 text-center">
                  <i className="ri-checkbox-circle-fill text-accent-500 text-5xl mb-4 block" />
                  <h3 className="font-heading text-xl font-bold text-foreground-800 mb-2">{t('contact_success')}</h3>
                  <p className="text-sm text-foreground-600">{t('contact_success_message')}</p>
                </div>
              ) : (
                <form data-readdy-form="" onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input type="text" name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder={t('contact_name')} className="w-full px-4 py-3 border border-background-300 rounded-sm text-sm focus:outline-none focus:border-accent-500" />
                    <input type="email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder={t('contact_email')} className="w-full px-4 py-3 border border-background-300 rounded-sm text-sm focus:outline-none focus:border-accent-500" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input type="tel" name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t('contact_phone_label')} className="w-full px-4 py-3 border border-background-300 rounded-sm text-sm focus:outline-none focus:border-accent-500" />
                    <input type="text" name="subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder={t('contact_subject')} className="w-full px-4 py-3 border border-background-300 rounded-sm text-sm focus:outline-none focus:border-accent-500" />
                  </div>
                  <textarea name="message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required placeholder={t('contact_message')} maxLength={500} rows={5} className="w-full px-4 py-3 border border-background-300 rounded-sm text-sm focus:outline-none focus:border-accent-500 resize-none" />
                  <button type="submit" className="px-8 py-3.5 bg-accent-500 text-primary-950 text-sm font-bold rounded-sm hover:bg-accent-400 transition-colors cursor-pointer uppercase tracking-wide whitespace-nowrap">
                    {t('contact_send')}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info Sidebar */}
            <div className="w-full lg:w-[380px] flex-shrink-0 space-y-6">
              {/* Quick Contact */}
              <div className="bg-primary-800 rounded-sm p-6">
                <h3 className="font-heading text-lg font-bold text-background-50 mb-4">{t('footer_contact_info')}</h3>
                <div className="space-y-4">
                  <a href="https://wa.me/201000932585" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-background-200 hover:text-accent-400 transition-colors cursor-pointer">
                    <div className="w-10 h-10 bg-accent-500/20 rounded-sm flex items-center justify-center">
                      <i className="ri-whatsapp-line text-accent-400 text-lg" />
                    </div>
                    <div>
                      <span className="block text-xs text-background-400">{t('contact_whatsapp')}</span>
                      <span className="text-sm font-medium">+20 100 093 2585</span>
                    </div>
                  </a>
                  <a href="tel:+201000932585" className="flex items-center gap-3 text-background-200 hover:text-accent-400 transition-colors cursor-pointer">
                    <div className="w-10 h-10 bg-accent-500/20 rounded-sm flex items-center justify-center">
                      <i className="ri-phone-line text-accent-400 text-lg" />
                    </div>
                    <div>
                      <span className="block text-xs text-background-400">{t('contact_call')}</span>
                      <span className="text-sm font-medium">+20 100 093 2585</span>
                    </div>
                  </a>
                  <a href="mailto:yallabinatour@gmail.com" className="flex items-center gap-3 text-background-200 hover:text-accent-400 transition-colors cursor-pointer">
                    <div className="w-10 h-10 bg-accent-500/20 rounded-sm flex items-center justify-center">
                      <i className="ri-mail-line text-accent-400 text-lg" />
                    </div>
                    <div>
                      <span className="block text-xs text-background-400">{t('contact_email_us')}</span>
                      <span className="text-sm font-medium">yallabinatour@gmail.com</span>
                    </div>
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-background-50 border border-background-200 rounded-sm p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">{t('contact_hours')}</h3>
                <p className="text-sm text-foreground-600">{t('contact_hours_text')}</p>
              </div>

              {/* Map */}
              <div className="rounded-sm overflow-hidden border border-background-200 h-[250px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14258.8846497649!2d33.8034!3d27.2579!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x145286ffdd9d7d7d%3A0x2b3b8e7c6f8e9a0!2sHurghada!5e0!3m2!1sde!2seg!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={t('contact_location')}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}